import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  TableSortLabel,
  Box,
  styled,
  Button,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { CreateButton } from '../../../components/buttons/CreateButton';
import { ConfirmDialog } from '../../../components/dialogs';
import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { Spinner } from '../../../components/spinners';
import { ArticleListStrings, Defaults } from '../../../constants';
import { useDeletePost } from '../../../lib/post/hooks/useDeletePost';
import useGetAllPosts from '../../../lib/post/hooks/useGetAllPosts';
import { SortDirection } from '../../../lib/util/helpers';

const PostsList = () => {
  const navigate = useNavigate();
  const handleAllReportedPosts = () => navigate(`/cms/posts/reported`);
  const handleEditPost = (id: number) => navigate(`/cms/posts/${id}`);
  const { mutateAsync: deletePost, isLoading: isLoadingDeletePost } = useDeletePost();
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;
  const searchString = search && { search };
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const {
    data: post,
    isLoading,
    error,
  } = useGetAllPosts({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
  });

  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const setNewPage = (page: number) => setQuery({ page: page - 1 });

  const sortToObj = (sortStr: string | undefined) => {
    const initialValues = { column: '', direction: undefined };
    if (typeof sortStr !== 'string') {
      return initialValues;
    }
    const [column, direction] = sortStr.split(' ');
    if (typeof column !== 'string' || !['asc', 'desc', undefined].includes(direction)) {
      return initialValues;
    }
    return { ...initialValues, column, direction: direction as SortDirection };
  };

  const sortObj = sortToObj(sort ?? '');

  const columns = [
    {
      text: 'Title',
      name: 'post.title',
    },
    {
      text: ArticleListStrings.CATEGORY_SUBCATEGORY,
      name: 'categorySubcategory',
      sortable: false,
    },
    {
      text: 'Author Username',
      name: 'author.username',
    },
    {
      text: 'Is Recommended',
      name: 'post.recommended',
    },
    {
      text: 'Date Override',
      name: 'post.modified',
    },
    {
      text: 'Actions',
      name: 'actions',
    },
  ];

  return (
    <Container>
      <FullWidthBar>
        <Typography variant="h5" sx={{ marginRight: '20px' }}>
          Articles
        </Typography>
        <CreateButton variant="contained" onClick={handleAllReportedPosts}>
          Reported Articles
        </CreateButton>
        <StyledSearchField
          searchString={search ?? ''}
          onSearch={(search: string) => {
            debouncedSearchFn(search);
          }}
        />
      </FullWidthBar>

      <TableContainer sx={{ backgroundColor: 'white' }}>
        <Table aria-labelledby="tableTitle">
          <TableHead>
            <PointerTableRow>
              {columns.map((column) => (
                <TableCell key={column.name}>
                  {column.sortable === false ? (
                    column.text
                  ) : (
                    <TableSortLabel
                      active={sortObj.column === column.name}
                      direction={sortObj.direction}
                      onClick={() => {
                        const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                        setNewSort(column.name, isAsc ? 'desc' : 'asc');
                      }}
                      component="a"
                    >
                      {column.text}
                      {sortObj.column === column.name ? (
                        <Box component="span" sx={visuallyHidden}>
                          {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </PointerTableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !error &&
              post.data &&
              post.data.map((row: any, index: number) => {
                const dateOverride = row.editAllowed ? new Date(row.editAllowed).toLocaleDateString() : '';
                return (
                  <StyledTableRow key={index}>
                    <TitleTableCell>{row.title}</TitleTableCell>
                    <TableCell>
                      {row.category?.title || ArticleListStrings.UNASSIGNED_CATEGORY}
                      {' / '}
                      {row.subCategory?.title || ArticleListStrings.UNASSIGNED_CATEGORY}
                    </TableCell>
                    <TableCell>{row.author.username}</TableCell>
                    <TableCell>{row.recommended ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{dateOverride}</TableCell>
                    <TableCell>
                      <Button onClick={() => handleEditPost(row.id)} type="button" style={{ textTransform: 'capitalize' }}>
                        Edit
                      </Button>

                      <ConfirmDialog
                        trigger={(open) => (
                          <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                            Delete
                          </Button>
                        )}
                        title="Delete Article"
                        description="Are you sure you want to delete this article?"
                        cancelBtnText={'Cancel'}
                        submitBtnText={'Submit'}
                        onConfirm={async (close) => {
                          await deletePost(row.id);
                          close();
                        }}
                        disabled={isLoadingDeletePost}
                        loading={isLoadingDeletePost}
                      />
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            {post.data.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={columns.length} style={{ textAlign: 'center' }}>
                  No posts records found.
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {post.total !== null && post.total !== 0 && (
        <FullWidthBar>
          <Typography variant="subtitle1">Total: {post.total}</Typography>
          <StyledPagination
            current={Number(page) + 1 || 1}
            total={Math.ceil(post.total / Defaults.LIST_PAGE_SIZE)}
            onPageChange={(page: number) => setNewPage(page)}
          />
        </FullWidthBar>
      )}
    </Container>
  );
};

const Container = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const TitleTableCell = styled(TableCell)({
  width: '280px',
  maxWidth: '280px',
  overflowWrap: 'anywhere',
});

const StyledTableRow = muiStyled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});

export default PostsList;
